/**
 * Application Keywords Constants
 * Centralized constants for all hardcoded strings/keywords to avoid magic strings throughout the application
 */

/**
 * Period Types
 */
export const PERIOD_TYPES = {
  DAILY: "DAILY",
  MONTHLY: "MONTHLY",
  QUARTERLY: "QUARTERLY",
  YEARLY: "YEARLY"
};

/**
 * Period Labels (for display)
 */
export const PERIOD_LABELS = {
  DAILY: "By day",
  MONTHLY: "By month",
  QUARTERLY: "By quarter",
  YEARLY: "By year"
};

/**
 * Period Types Array for dropdowns
 */
export const PERIOD_TYPES_ARRAY = [
  { label: PERIOD_LABELS.YEARLY, value: PERIOD_TYPES.YEARLY },
  { label: PERIOD_LABELS.QUARTERLY, value: PERIOD_TYPES.QUARTERLY },
  { label: PERIOD_LABELS.MONTHLY, value: PERIOD_TYPES.MONTHLY },
  { label: PERIOD_LABELS.DAILY, value: PERIOD_TYPES.DAILY }
];

/**
 * Forecast Edit Value screen view modes (rolling 90-day window vs single month)
 */
export const EDIT_VALUE_VIEW_MODES = {
  NINETY_DAY: "90DAY",
  MONTH: "MONTH"
};

export const EDIT_VALUE_VIEW_MODE_LABELS = {
  NINETY_DAY: "90 Day",
  MONTH: "Month by Month"
};

/**
 * Account Type Names
 */
export const ACCOUNT_TYPES = {
  STATISTIC: "Statistic",
  WAGES: "Wages"
};

/**
 * Detailed Income Statement view period.
 * Monthly = 12 month columns + Total; Quarterly = 4 quarter columns + Total.
 */
export const DIS_VIEW_PERIOD = {
  MONTHLY: "Monthly",
  QUARTERLY: "Quarterly",
};

/**
 * TPX-2680 / TPX-2756 — optional "Additional Data" on the Detailed Income Statement-V2 (report type 25), sent as
 * `reportParams.additionalColumns`. Values MUST match the backend `AdditionalColumnType` enum. PCT_OF_REVENUE and POR
 * add columns; PCT_OF_GOP (GOP %) and FLEX_FLOW (GOP Flex / Flow) are Summary rows after GOP and add no columns.
 * % Change is not an option: the backend adds it after each Variance whenever a comparison is present.
 */
export const DIS_ADDITIONAL_COLUMN_TYPES = {
  PCT_OF_REVENUE: "PCT_OF_REVENUE",
  PCT_OF_GOP: "PCT_OF_GOP",
  POR: "POR",
  FLEX_FLOW: "FLEX_FLOW",
};

/** TPX-2756 — tooltips shown while a comparison-dependent option is disabled. */
export const DIS_NEEDS_COMPARISON_TOOLTIP = {
  FLEX_FLOW: "Add a comparison to show GOP Flex / Flow",
};

/** TPX-2680 / TPX-2756 — the options that add columns (% of Revenue, POR). Either one enables the scope switch. */
export const DIS_ADDITIONAL_COLUMN_OPTIONS = [
  { value: DIS_ADDITIONAL_COLUMN_TYPES.PCT_OF_REVENUE, label: "% of Revenue" },
  { value: DIS_ADDITIONAL_COLUMN_TYPES.POR, label: "POR" },
];

/**
 * TPX-2756 — the options that add a Summary row after GOP (GOP %, GOP Flex / Flow). They never add columns. GOP % needs
 * no comparison; Flex / Flow does.
 */
export const DIS_ADDITIONAL_ROW_OPTIONS = [
  { value: DIS_ADDITIONAL_COLUMN_TYPES.PCT_OF_GOP, label: "GOP %" },
  {
    value: DIS_ADDITIONAL_COLUMN_TYPES.FLEX_FLOW,
    label: "GOP Flex / Flow",
    requiresComparison: true,
    disabledTooltip: DIS_NEEDS_COMPARISON_TOOLTIP.FLEX_FLOW,
  },
];

/** TPX-2680 — the GOP Flex / Flow row option (enabled only when a comparison is added). */
export const DIS_FLEX_FLOW_OPTION = DIS_ADDITIONAL_ROW_OPTIONS[1];

/**
 * TPX-2756 — every checkbox in dialog order, grouped: the column options (% of Revenue, POR), then the GOP Summary
 * rows (GOP %, GOP Flex / Flow).
 */
export const DIS_ADDITIONAL_DATA_OPTIONS = [...DIS_ADDITIONAL_COLUMN_OPTIONS, ...DIS_ADDITIONAL_ROW_OPTIONS];

/** TPX-2680 / TPX-2756 — every selectable Additional Data value, in dialog order (the backend re-sorts by its enum). */
export const DIS_ADDITIONAL_DATA_VALUES = DIS_ADDITIONAL_DATA_OPTIONS.map((o) => o.value);

/** TPX-2680 — where the ratio columns appear, sent as `reportParams.additionalColumnsScope`. */
export const DIS_ADDITIONAL_COLUMNS_SCOPE = {
  MONTHLY_AND_TOTAL: "MONTHLY_AND_TOTAL",
  TOTAL_ONLY: "TOTAL_ONLY",
};

/** TPX-2680 — labels for the scope switch. */
export const DIS_ADDITIONAL_COLUMNS_SCOPE_OPTIONS = [
  { value: DIS_ADDITIONAL_COLUMNS_SCOPE.MONTHLY_AND_TOTAL, label: "Each Month + Total" },
  { value: DIS_ADDITIONAL_COLUMNS_SCOPE.TOTAL_ONLY, label: "Total Only" },
];

/**
 * Custom plan-type sentinels shared by the Detailed Income Statement-V2 (report type 25) and the
 * Month By Month Plan Summary (report type 6). Sent as `planTypeName`; both defer plan selection to
 * the backend resolve rule and need no user-selected plan or plan year:
 *  - BEST_CASE: backend Best-Case resolve (published Forecast → published Budget), actuals blended
 *    into closed months.
 *  - TRAILING_12_MONTH: trailing-12 actuals window ending at the closed accounting month.
 */
export const CUSTOM_DIS_PLAN_TYPE = {
  BEST_CASE: "BEST_CASE",
  TRAILING_12_MONTH: "TRAILING_12_MONTH",
};

/**
 * Plan-type options for the Month By Month Plan Summary export (report type 6 / bf104). TPX-2500:
 * mirrors the Detailed Income Statement-V2 set minus Actuals — Budget / Forecast / Trailing 12 Month /
 * Best Case / What-If. Budget/Forecast/What if resolve to plan_type ids 1/2/3 on the backend;
 * TRAILING_12_MONTH and BEST_CASE are scenario tokens (no plan_type id, no plan year). Shared by the
 * exports page and the download dialog; kept separate from the reused basePlanTypeOptions.
 */
export const BF104_PLAN_TYPE_OPTIONS = [
  { value: "Budget", label: "Budget" },
  { value: "Forecast", label: "Forecast" },
  { value: CUSTOM_DIS_PLAN_TYPE.TRAILING_12_MONTH, label: "Trailing 12 Month" },
  { value: CUSTOM_DIS_PLAN_TYPE.BEST_CASE, label: "Best Case" },
  { value: "What if", label: "What-If" },
];

/** bf104 plan-type tokens that resolve on the backend without a plan year. */
export const BF104_NO_PLAN_YEAR_TYPES = new Set([
  CUSTOM_DIS_PLAN_TYPE.TRAILING_12_MONTH,
  CUSTOM_DIS_PLAN_TYPE.BEST_CASE,
]);

/** True when the selected bf104 plan type requires a plan-year selection (Budget / Forecast / What-If). */
export const bf104RequiresPlanYear = (planType) =>
  !!planType && !BF104_NO_PLAN_YEAR_TYPES.has(planType);

/**
 * Maximum number of properties allowed for a live (in-browser) report download.
 * Above this count the live `export-report` request risks timing out on large data, so the
 * Download button is disabled and the user is directed to the "Send on mail" option instead.
 */
export const MAX_DIRECT_DOWNLOAD_PROPERTIES = 10;

/**
 * Detailed Income Statement-V2 (report type 25) is a large, multi-series report, so its live-download
 * property limit is lower than the general one. Above this count the Download button is disabled and the
 * user is directed to "Send on mail".
 */
export const MAX_DIS_DOWNLOAD_PROPERTIES = 5;

/**
 * Plan Status Types
 */
export const PLAN_STATUS = {
  DRAFT: "draft",
  PUBLISHED: "published",
  FINAL: "final",
  BUDGET: "budget",
  FORECAST: "forecast",
  WHAT_IF: "what if",
  OVERDUE: "overdue",
  REACHING_SOON: "reaching soon",
  ENOUGH_TIME: "enough time"
};

/**
 * Plan Initialization Status Types
 */
export const PLAN_INIT_STATUS = {
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  PROCESSING: "PROCESSING",
  IN_PROGRESS: "IN_PROGRESS",
  NOT_STARTED: "NOT_STARTED",
  UNKNOWN: "UNKNOWN",
  /** No init job / status record for this plan — treat as ready to open plan details. */
  NOT_FOUND: "NOT_FOUND",
};

/**
 * Job/Process Status Types
 */
export const JOB_STATUS = {
  COMPLETED: "COMPLETED",
  FAILED: "FAILED",
  PROCESSING: "PROCESSING",
  IN_PROGRESS: "IN_PROGRESS"
};

/**
 * Month Names (abbreviated)
 */
export const MONTH_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun",
  "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"
];

/**
 * Month Names (full)
 */
export const MONTH_NAMES_FULL = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December"
];

/**
 * Standard Hours Period Types
 */
export const STANDARD_HOURS_PERIOD = {
  DAY: "day",
  WEEK: "week",
  MONTH: "month"
};

/**
 * Standard Hours Period Array for dropdowns
 */
export const STANDARD_HOURS_PERIOD_ARRAY = [
  { label: "Day", value: STANDARD_HOURS_PERIOD.DAY },
  { label: "Week", value: STANDARD_HOURS_PERIOD.WEEK },
  { label: "Month", value: STANDARD_HOURS_PERIOD.MONTH }
];

/**
 * Toast Message Types
 */
export const TOAST_TYPES = {
  SUCCESS: "success",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info"
};

export const ACCOUNT_REVERSAL_MONTH_ONLY_MESSAGE =
  "Account reversal supports Month input only. Input by has been set to Month.";

// Edit Driver check failed and the server sent no message of its own (e.g. an empty 4xx, or an error thrown in the dialog).
export const EDIT_DRIVER_CHECK_FAILED_MESSAGE =
  "Unable to apply the selected driver config. Please review it and try again.";

export const TOAST_LAYOUT = {
  TOP_NOTICE_CLASS: "tpx-top-right-notice",
  RIGHT_OFFSET_PX: 24,
  STACK_TOP_DEFAULT_PX: 60,
  STACK_GAP_PX: 8,
  TOAST_WIDTH_PX: 380,
  EXIT_TRANSITION_MS: 200,
  DEFAULT_DURATION_MS: 6000,
};

/**
 * MCR Excel Upload Modes
 */
export const UPLOAD_MODES = {
  MISSING_DRIVER: "MISSING_DRIVER",
  DO_NOT_USE: "DO_NOT_USE",
  ROUNDING_CORRECTION: "ROUNDING_CORRECTION",
};

/**
 * Paired-daily dedup correction utility
 */
export const PAIRED_DAILY_DEDUP_MODES = {
  DRY_RUN: "DRY_RUN",
  DIRECT: "DIRECT",
};

export const PAIRED_DAILY_DEDUP_DECISIONS = {
  SKIP: "SKIP",
  SET_ON_DAILY: "SET_ON_DAILY",
};

export const PAIRED_DAILY_DEDUP_BUCKETS = {
  ALREADY_CLEAN: "ALREADY_CLEAN",
  LATEST_ALREADY_CORRECT: "LATEST_ALREADY_CORRECT",
  CORRECTED: "CORRECTED",
  MONTHLY_MISMATCH_NEEDS_DECISION: "MONTHLY_MISMATCH_NEEDS_DECISION",
  MULTIPLE_CONSISTENT_GENERATIONS: "MULTIPLE_CONSISTENT_GENERATIONS",
  NO_CONSISTENT_PAIR: "NO_CONSISTENT_PAIR",
};

export const PAIRED_DAILY_DEDUP_BUCKET_LABELS = {
  ALREADY_CLEAN: "Already clean",
  LATEST_ALREADY_CORRECT: "Latest already correct",
  CORRECTED: "Corrected",
  MONTHLY_MISMATCH_NEEDS_DECISION: "Monthly mismatch — needs decision",
  MULTIPLE_CONSISTENT_GENERATIONS: "Multiple consistent generations — review",
  NO_CONSISTENT_PAIR: "No consistent pair",
};

/**
 * HTTP Status Codes (commonly used)
 */
export const HTTP_STATUS = {
  OK: 200,
  CREATED: 201,
  NO_CONTENT: 204,
  BAD_REQUEST: 400,
  UNAUTHORIZED: 401,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  INTERNAL_SERVER_ERROR: 500
};

/**
 * How long after startup the plan page access check may reuse the startup `GET plan/{id}` response (TPX-2675)
 */
export const PLAN_BOOTSTRAP_TTL_MS = 30000;

export const PLAN_ACCESS_STRINGS = {
  NO_ACCESS: "You don't have access to this plan",
  NOT_FOUND: "Plan not found"
};

/**
 * Maximum character length for a plan note.
 * Shared between the create and edit schemas so the limit is defined once.
 */
export const NOTE_MAX_LENGTH = 200;

/**
 * User-facing strings for the Notes drawer (create, edit, delete).
 * All toast messages, dialog copy, and validation messages live here.
 */
export const NOTE_STRINGS = {
  TOAST_UPDATED: "Note updated",
  TOAST_DELETED: "Note deleted",
  TOAST_ERROR_UPDATE: "Error updating note",
  TOAST_ERROR_DELETE: "Error deleting note",
  TOAST_ERROR_FETCH: "Couldn't fetch notes for this line item.",
  TOAST_ERROR_SAVE: "Error saving note",
  DELETE_DIALOG_TITLE: "Delete note",
  DELETE_DIALOG_MESSAGE: "Are you sure you want to delete this note? This action cannot be undone.",
  DELETE_DIALOG_CANCEL: "Cancel",
  DELETE_DIALOG_CONFIRM: "Delete",
  VALIDATION_REQUIRED: "Note is required",
  VALIDATION_MAX_LENGTH: "Max characters count exceeded",
  TOAST_ERROR_NOT_FOUND: "Resource not found.",
  UPDATED_LABEL: "Updated",
};

/**
 * Common String Values
 */
export const COMMON_STRINGS = {
  UNKNOWN: "Unknown",
  ALL: "all",
  TRUE: "true",
  FALSE: "false"
};

/**
 * Multi-line bulk value update (Update values → Manually)
 */
export const BULK_VALUE_UPDATE_STRINGS = {
  TOOLBAR_BULK_UPDATE: "Bulk value update",
  UPDATE_VALUES: "Update values",
  VIA_IMPORT: "Via import",
  MANUALLY: "Manually",
  MODAL_TITLE: "Bulk value update",
  CANCEL: "Cancel",
  SAVE: "Update values",
  HIDE_ZERO_ROWS: "Hide '0' rows",
  ADJUSTMENTS: "Adjustments",
  SHOW_COMPARISON: "Comparisons",
  ADJUSTMENT_LABEL: "Adjustment",
  REVENUE_LABEL: "Total",
  COMPARISON_LABEL: "Comparison",
  NO_DATA: "No data available.",
  EDIT_IN_VIEW_HINT: "Edit in {period} view",
  BANNER_UNSUPPORTED:
    "Only “Input By” and “Paired driver” line items are supported for line items value bulk update. Any other selected line items are not supported and will be skipped.",
  TOAST_MIXED_PERIOD:
    "Select line items with the same value period — all monthly, all quarterly, all yearly, or all daily — to update values together.",
  TOAST_NO_ELIGIBLE:
    "None of the selected rows use a supported driver. Only Input by and Paired driver can be updated manually.",
  TOAST_MAX_ROWS:
    "You can update up to {max} line items at a time. Reduce your selection and try again.",
  TOAST_NO_SELECTION: "Select at least one line item to update values manually.",
  TOAST_SAVE_SUCCESS: "Values updated successfully.",
  TOAST_SAVE_ERROR: "Failed to update values. Please try again.",
  TOAST_VALIDATION: "Resolve invalid values before saving.",
  TOAST_NO_EDITABLE: "No editable lines to save.",
  SAVING_MESSAGE: "Updating values...",
  SAVING_MESSAGE_DEPENDENTS: "Updating dependent line-items — just a moment…",
  SAVING_MESSAGE_DRAFT: "Creating a draft for updated line-items…",

  BULK_UPDATE_TRIGGER: "Bulk update",
  SELECT_ALL: "Select all",
  DESELECT_ALL: "Deselect all",
  OPERATION_SELECTOR_LABEL: "Operation selector",
  VALUE_LABEL: "Value",
  PANEL_UPDATE: "Update",
  PANEL_CANCEL: "Cancel",
  START_DATE_LABEL: "Start date*",
  END_DATE_LABEL: "End date*",
  MSG_EVERY_MONTH: "Values will be applied to every month.",
  MSG_EVERY_QUARTER: "Values will be applied to every quarter.",
  MSG_FULL_YEAR: "Values will be applied to the full year.",
  MSG_APPLIED_TO_MONTHS: "Values will be applied to {months} month{plural}.",
  MSG_APPLIED_TO_QUARTERS: "Values will be applied to {quarters} period{plural}.",
  MSG_APPLIED_TO_DAILY: "Values will be applied to {days} between {start} and {end}.",
  MSG_NO_PERIOD_SELECTED: "Select at least one period to apply the operation.",
}

/** Values must match the literal strings applyBulkOperation / applyBulkOpMath compare against. */
export const BULK_OPERATIONS = {
  SET: "Set",
  INCREASE: "Increase",
  DECREASE: "Decrease",
  INCREASE_FROM_LAST_YEAR: "Increase from last year",
  DECREASE_FROM_LAST_YEAR: "Decrease from last year",
};

/** #/% toggle values for the Bulk update operation tool. */
export const BULK_OPERATION_TOGGLE = {
  HASH: "#",
  PERCENT: "%",
};

/** Max line items that can be updated in one manual bulk session. */
export const BULK_VALUE_UPDATE_MAX_ROWS = 4000;

/**
 * Root toolbar menu that groups every bulk action (TPX-2419).
 * The group labels reuse BULK_VALUE_UPDATE_STRINGS.TOOLBAR_BULK_UPDATE and
 * BULK_LOCK_STRINGS.TOOLBAR_BULK_LOCK, so only the root button label lives here.
 */
export const BULK_UPDATE_MENU_STRINGS = {
  TOOLBAR_BULK_UPDATE: "Bulk update",
};

/** Copy for bulk lock / unlock of line items and drivers (TPX-2419). */
export const BULK_LOCK_STRINGS = {
  TOOLBAR_BULK_LOCK: "Bulk lock",
  LOCK_LINES: "Lock line items",
  UNLOCK_LINES: "Unlock line items",
  LOCK_DRIVERS: "Lock drivers",
  UNLOCK_DRIVERS: "Unlock drivers",
  CANCEL: "Cancel",
  REASON_LABEL: "Reason (optional)",
  REASON_PLACEHOLDER: "e.g. Closing FY budget",
  TITLE_LOCK_LINES: "Lock selected line items?",
  TITLE_UNLOCK_LINES: "Unlock selected line items?",
  TITLE_LOCK_DRIVERS: "Lock selected drivers?",
  TITLE_UNLOCK_DRIVERS: "Unlock selected drivers?",
  BLURB_LOCK_LINE:
    "Locking these line items prevents edits to both their values and their drivers.",
  BLURB_LOCK_DRIVER:
    "Locking these drivers prevents edits to their drivers. The values remain editable.",
  BLURB_UNLOCK_LINE:
    "Releasing these locks will allow edits to the line items' values and drivers again. The lock history is preserved.",
  BLURB_UNLOCK_DRIVER:
    "Releasing these locks will allow edits to the drivers again. The lock history is preserved.",
  SELECTED_COUNT: "{count} line item(s) selected.",
  SUBMIT_LOCK: "Lock",
  SUBMIT_UNLOCK: "Unlock",
  SUBMITTING_LOCK: "Locking",
  SUBMITTING_UNLOCK: "Unlocking",
  TOAST_NO_SELECTION: "Select at least one line item to lock or unlock.",
  TOAST_LOCK_ERROR: "Failed to lock the selected line items. Please try again.",
  TOAST_UNLOCK_ERROR: "Failed to unlock the selected line items. Please try again.",
  SKIP_REASON_NO_PERMISSION: "no permission",
  SKIP_REASON_ALREADY_LOCKED: "already locked",
  SKIP_REASON_NOT_LOCKED: "not locked",
};

export const COA_SORT_ORDER_SOURCE = {
  TEMPLATE: "OTELIER_STRUCTURE_TEMPLATE",
  PREVIEW: "COA_DRAFT_PREVIEW",
};

// TPX-2071: the Settings "Internal settings" tab is Otelier-only.
export const OTELIER_EMAIL_DOMAIN = "@otelier.io";

export const isOtelierUserEmail = (email) =>
  typeof email === "string" && email.toLowerCase().endsWith(OTELIER_EMAIL_DOMAIN);

// TPX-2071: fired after the internal settings are saved so SupportFAB re-reads the Trudy toggle without a reload.
export const INTERNAL_SETTINGS_UPDATED_EVENT = "tpx-internal-settings-updated";

// TPX-2071: Jira board feedback tickets go to (per HMG). Backend default is TPX_BOARD.
export const FEEDBACK_DESTINATION = {
  TPX_BOARD: "TPX_BOARD",
  SUPPORT: "SUPPORT",
};

export const FEEDBACK_DESTINATION_OPTIONS = [
  { value: FEEDBACK_DESTINATION.TPX_BOARD, label: "TPX board" },
  { value: FEEDBACK_DESTINATION.SUPPORT, label: "Support board" },
];

export const BULK_CLOSED_CONFIRM = {
  HEADING: "This bulk value update includes some closed months. Are you sure you want to update the closed months' values?",
  BTN_CANCEL: "Cancel",
  BTN_UNCLOSED_ONLY: "No, update only unclosed month values",
  BTN_UPDATE_ALL: "Yes, update all",
};

export const DRAG_DROP_CLOSED_CONFIRM = {
  HEADING: "The target card(s) contain one or more closed months. Do you want to override the values for those closed months?",
  HEADING_DAILY: "One or more days in the selected range fall within a closed month. Do you want to override those values?",
  BTN_CANCEL: "Cancel",
  BTN_UNCLOSED_ONLY: "No, override only unclosed month values",
  BTN_OVERRIDE_ALL: "Yes, override all",
};

export const CLOSED_MONTH_DIALOG = {
  HEADER_PREFIX: "Last closed month:",
  BODY_DEFAULT: "Are you sure you want to update closed month values?",
  BODY_DAILY: "This day is in a closed month. Are you sure you want to update this value?",
  BODY_QUARTER_SUFFIX: "Are you sure you want to update values for closed months?",
  BODY_YEAR_SUFFIX: "Are you sure you want to update values for closed months?",
  BODY_ALL_MONTHS: "all months",
  ARIA_LABEL_NO: "No, cancel edit",
  ARIA_LABEL_YES: "Yes, proceed with edit",
};

/**
 * Plan multi-comparison (TPX-1903)
 * Up to MAX_COMPARISONS comparisons can be configured per plan. Each comparison is keyed across all
 * surfaces by its `planComparisonName` (the backend-verified unique key — the response `id` is the
 * plan being compared, identical on every entry, and is NOT a per-comparison identifier).
 */
export const MAX_COMPARISONS = 3;

/** Default name seeded into a new comparison row: "Comparison 1", "Comparison 2", ... */
export const COMPARISON_DEFAULT_NAME_PREFIX = "Comparison";

/**
 * Configure-comparison target types (UI radio → derived `dataType` sent to the backend).
 * "Specific plan" saves as dataType "Plan"; Actuals and Snapshot save as-is.
 */
export const COMPARISON_TARGET = {
  ACTUALS: "actuals",
  SPECIFIC: "specific",
  SNAPSHOT: "snapshot",
  // TPX-1902: rolling trailing-12-months-of-actuals target (Edit Plan).
  TRAILING_12: "trailing12",
};

export const COMPARISON_DATA_TYPE = {
  ACTUALS: "Actuals",
  PLAN: "Plan",
  SNAPSHOT: "Snapshot",
  // TPX-1902: persisted dataType — must byte-match backend Constants.PLAN_COMPARISON_TYPE_TRAILING_12.
  TRAILING_12: "Trailing 12 month",
};

/** Placement checkbox labels — exact Figma wording (TPX-1903). */
export const COMPARISON_PLACEMENT_LABELS = {
  HIGHLIGHTS: 'Show in "Plan highlights"',
  ROLLUPS: 'Show in "Roll-up"',
  PLAN_TABLE: "Show in Plan table",
};

/** Display-toggle labels (single global choice: numerical difference vs percentage). */
export const COMPARISON_DISPLAY_LABELS = {
  NUMERICAL: "Show as numerical difference",
  PERCENTAGE: "Show as percentage",
};

/** Plan-details grid comparison group child-column headers. */
export const COMPARISON_COLUMN_HEADERS = {
  COMP: "Comp",
  VAR: "Var",
  VAR_PERCENT: "Var %",
};

export const ACCOUNT_GROUPS_MAX_DEPTH = 20;
export const ACCOUNT_GROUPS_NEW_GROUP_BASE_NAME = "New Group";
export const ACCOUNT_GROUPS_DRAG_HINT_KEY = "accountGroupsDragHintDismissed";
// Default report-inclusion state for a SKELETON section / GROUPING group (false = included).
export const ACCOUNT_GROUPS_EXCLUDE_FROM_REPORT_DEFAULT = false;

export const ROW_TYPE = {
  SKELETON: "SKELETON",
  GROUPING: "GROUPING",
  COA: "COA",
};

// COA Structure Excel import (TPX-1732) — matches CustomPnlViewExcelConstants on the backend.
export const COA_IMPORT_STATUS = {
  ALL: "ALL",
  PARTIAL: "PARTIAL",
  NONE: "NONE",
};

export const COA_IMPORT_WARNING_LABELS = {
  INVALID_COA_CODE: "COA code not found",
  INVALID_PLACEMENT: "Not allowed under This section",
  UNKNOWN_SECTION: "Unknown section — could not place",
  NOT_FULLY_MAPPED: "Not mapped to a structure code — map it first",
};

export const ACCOUNT_TYPE_BADGE = {
  Revenue:            { letter: "R", color: "#22C55E", bg: "#22C55E29" },
  "Other Expenses":   { letter: "E", color: "#FF5630", bg: "#FF563029" },
  "Cost of Sales":    { letter: "C", color: "#B76E00", bg: "#FFAB0029" },
  "Payroll & Related":{ letter: "P", color: "#8E33FF", bg: "#8E33FF29" },
  Statistical:        { letter: "S", color: "#637381", bg: "#919EAB29" },
  "Other Income":     { letter: "O", color: "#2A5BA2", bg: "#2A5BA229" },
};

export const DND_ITEM_TYPE = {
  ACCOUNT_FROM_GRID: "ACCOUNT_FROM_GRID",
  GROUPING_NODE: "GROUPING_NODE",
};

// --- Account Groups drop restrictions (mirror of backend com.tpx.Constants) ---
// operatingDepartmentIds = roomsDepartmentIds(1,38) + fnbDepartmentIds(2,3,4,28) + miscDepartmentIds(10,33)
export const AG_OPERATING_DEPARTMENT_IDS = [1, 2, 3, 4, 10, 28, 33, 38];
export const AG_ACCOUNT_TYPE_IDS = {
  COST_OF_SALES: 49,
  OTHER_EXPENSES: 50,
  OTHER_INCOME: 51,
  PAYROLL_AND_RELATED: 52,
  REVENUE: 53,
  STATISTICAL: 54,
};
// Expense account types that go under Departmental Expenses for operating departments.
export const AG_EXPENSE_ACCOUNT_TYPE_IDS = [49, 50, 52];
// Root SKELETON sections that accept Statistical accounts (and only those).
export const AG_STATISTICAL_SECTIONS = ["Rooms Available", "Rooms Rented", "Statistics"];
export const AG_SECTION_OPERATING_REVENUE = "Operating Revenue";
export const AG_SECTION_DEPARTMENTAL_EXPENSES = "Departmental Expenses";
export const AG_SECTION_REPLACEMENT_RESERVE = "Replacement Reserve";
// Financial-statement-location display name that pins an account to Replacement Reserve.
export const AG_FSL_REPLACEMENT_RESERVE = "Replacement Reserve";

// Maps the placement-rules API section keys to their skeleton root `coaName`.
// Used to translate the API response into a coaName-keyed rule lookup.
export const AG_PLACEMENT_RULE_KEY_TO_SECTION = {
  roomsAvailable: "Rooms Available",
  roomsRented: "Rooms Rented",
  statistics: "Statistics",
  operatingRevenue: "Operating Revenue",
  departmentalExpenses: "Departmental Expenses",
  undistributedOperatingRevenue: "Undistributed Operating Revenue",
  undistributedOperatingExpenses: "Undistributed Operating Expenses",
  managementFees: "Management Fees",
  nonOperatingIncome: "Non-Operating Income",
  nonOperatingExpenses: "Non-Operating Expenses",
  interestDepreciationAndAmortization: "Interest, Depreciation & Amortization",
  tax: "Tax",
  replacementReserve: "Replacement Reserve",
};


/**
 * Critique snapshot status (TBL_CRITIQUE.SNAPSHOT_STATUS).
 * PENDING while the critique's data is still being written after creation, CREATED once it is in.
 */
export const CRITIQUE_SNAPSHOT_STATUS = {
  PENDING: "PENDING",
  CREATED: "CREATED",
};

/** How often the critique page re-checks a PENDING critique until its data is ready. */
export const CRITIQUE_SNAPSHOT_POLL_INTERVAL_MS = 15000;

export const CRITIQUE_PREPARING_KPI_MESSAGE = "Preparing KPI data…";
export const CRITIQUE_PREPARING_PL_MESSAGE = "Preparing P&L data…";

export const CRITIQUE_PREPARING_MESSAGE =
  "This critique is still being prepared. KPI and P&L data will appear here automatically once it is ready — this can take a few minutes.";

/** Most critiques one delete can include; must match the backend's Constants.CRITIQUE_BULK_DELETE_MAX (TPX-2730). */
export const CRITIQUE_DELETE_MAX = 200;
export const CRITIQUE_DELETE_MAX_MESSAGE = `You can delete up to ${CRITIQUE_DELETE_MAX} critiques at a time. Select fewer and try again.`;

/**
 * Critique template "Always include" / "Always exclude" pickers (TPX-2735).
 * Card titles, option kinds and every user-facing string of the mutual-exclusion rules.
 */
export const CRITIQUE_ALWAYS_LISTS = {
  INCLUDE_TITLE: "Always-visible departments & accounts",
  EXCLUDE_TITLE: "Always exclude accounts",
  SELECT_ALL_OPTION_ID: "select-all",
  SELECT_ALL_LABEL: "Select All",
  OPTION_KIND: { DEPARTMENT: "dept", SUB_DEPARTMENT: "sub" },
  LOADING_PLACEHOLDER: "Loading…",
  UNTICKED_MESSAGE_PREFIX: "Removed",
  UNTICKED_ITEM_SINGULAR: "item",
  UNTICKED_ITEM_PLURAL: "items",
  UNTICKED_MESSAGE_SUFFIX:
    "Accounts and departments cannot be in both Always include and Always exclude.",
  UNTICKED_TOAST_DURATION_MS: 10000,
  LOAD_FAILED_MESSAGE:
    "Unable to load the account and department data for these lists. Reload the page to try again; both lists are read-only until then.",
};

/**
 * TPL → TPX Property Mapping tab (TPX-2147) on the Data Migration screen.
 * All user-facing strings for the tab live here so nothing is hardcoded in the component.
 */
export const PROPERTY_MAPPING = {
  TAB_LABEL: "Property mapping",
  SOURCE_BANNER_PREFIX: "TPL source database:",
  NOT_CONFIGURED: "Not configured — contact an administrator",
  // Backend signals "org has no TPL DB" as an error response carrying this message instead of a
  // null body; matched so the tab shows the not-configured banner rather than an error toast.
  NO_TPL_DB_SIGNATURE: "No TPL database configured",
  TPL_UNAVAILABLE: "TPL source unavailable in this environment",
  COL_TPX_PROPERTY: "TPX property",
  COL_TPL_PROPERTY: "TPL property",
  COL_ACTIONS: "Actions",
  TPL_PLACEHOLDER: "Select a TPL property",
  SAVE: "Save",
  DELETE: "Delete",
  NO_PROPERTIES: "No TPX properties found for this organization.",
  // Suffix for a mapping whose TPX property is no longer in the HMG property list; the row is shown
  // as "<tpxPropId> DELETED IN HMG" so the operator can still see and delete the stale mapping.
  DELETED_IN_HMG: "DISABLED FOR TPX",
  NO_ORG: "No organization selected. Choose an HMG in the sidebar.",
  TOAST_SAVE_SUCCESS: "Property mappings saved.",
  TOAST_DELETE_SUCCESS: "Mapping deleted.",
  TOAST_NO_ROWS: "Select a TPL property for at least one row before saving.",
  ERROR_LOAD: "Could not load property mappings.",
  ERROR_SAVE: "Could not save property mappings.",
  ERROR_DELETE: "Could not delete the mapping.",
  ERROR_TPL_LOAD: "Could not load TPL properties.",
};

/**
 * TPX-2793 — Critique STR section (Property / Comp Set / STR Index per period group).
 * Group keys match the backend critique.str_data v2 contract (Constants.STR_GROUP_*).
 */
export const STR_GROUP_LABELS = { WEEK: "Week", MTD: "MTD", QTD: "QTD", YTD: "YTD" };
export const STR_PERIOD_GROUP_KEYS = {
  weekly: ["WEEK", "MTD"],
  monthly: ["MTD", "YTD"],
  quarterly: ["QTD", "YTD"],
};
export const STR_DEFAULT_PERIOD = "monthly";
export const STR_KPI_ROWS = [
  { key: "occupancy", label: "Occupancy", isPercent: true },
  { key: "adr", label: "ADR", isPercent: false },
  { key: "revpar", label: "RevPAR", isPercent: false },
];
export const STR_COLUMNS = [
  { key: "property", label: "Property", isPercent: true },
  { key: "compSet", label: "Comp Set", isPercent: true },
  { key: "index", label: "STR Index", isPercent: false },
];
export const STR_EMPTY_VALUE = "-";
export const STR_DECIMALS = 2;
export const STR_PERCENT_SUFFIX = "%";
export const STR_HEADER_SEPARATOR = " · ";
export const STR_DATE_RANGE_SEPARATOR = " – ";
export const STR_MONTH_SHORT_NAMES = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
];
export const STR_REVIEW_TITLE = "STR review";
export const STR_NO_DATA_MESSAGE = "Property-level data will appear here per Critique";

/**
 * TPX-2002 — Plan Highlights: GOP % and GOP Flex / Flow badges on the Gross operating profit card.
 * All badge labels and tooltip strings live here so nothing is hardcoded in PlanHighlights.js.
 */
export const PLAN_HIGHLIGHT_GOP_RATIOS = {
  FLOW_LABEL: "Flow",
  FLEX_LABEL: "Flex",
  GOP_TOOLTIP_TITLE: "GOP % of operating revenue",
  FLOW_TOOLTIP_PREFIX: "Flow Through vs",
  FLEX_TOOLTIP_PREFIX: "Flex vs",
  FLEX_FLOW_TOOLTIP_PREFIX: "Flex / Flow vs",
  TITLE_SEPARATOR: " — ",
  NOT_AVAILABLE: "N/A",
  ITEM_SEPARATOR: "|",
};

/** Generic placeholder for critique commentary editors (GM editor + template explainer box). */
export const CRITIQUE_EXPLANATION_PLACEHOLDER = "Enter your critique explanation...";
